/* ===================================================================
   THE LABEL'S CYLINDER, AND IT IS WRITTEN HERE ONCE.

   A wine label is printed flat and then wrapped round a bottle, so its top edge
   bows up at the corners and its bottom edge sags. This file is the arithmetic that
   does that to a picture, and it is a file of its own for one reason: **two surfaces
   now wear the same label and neither may restate the curve.** `assets/bottle-stage.js`
   puts it on the configurator's bottles; `assets/hero-scene.js` puts it on the home
   page hero's. They call this; they do not carry copies of it (`D-188`).

   **A cylinder is not an affine transform, so the printing used to be painted once
   per vertical band.** `snippets/bottle-stage.liquid` rendered a ladder of spans
   inside every label — thirty-two on the lead bottle and sixteen on each one behind
   it, 240 across the three server-rendered packages — and `assets/generator.css`
   clipped each to its own band, scaled it to the height that band has on the
   cylinder, and cross-faded it into its neighbour so the joins read as ramps rather
   than steps. It is one bitmap now, warped exactly, and the ladder is gone.

   **This is not a performance fix, and writing it up as one would undo the work that
   established otherwise.** Measured: the ladder costs about 0.015ms a frame, and a
   six-bottle group with all 112 slices painting scrolls indistinguishably from the
   same page with the whole stage deleted from the document, at six times slower than
   a development Mac (`.impeccable/shape/2026-08-20-label-warp-evaluation.md`). The
   half-second stall the client reported was a `data:` URL, and that is already fixed
   (`D-122`). **Do not cite the warp as a performance problem without re-measuring it.**

   **What the bake actually buys**, in the order the plan justifies it:

   1. **An exact curve.** Every output column carries its own scale, so the
      displacement is the parabola rather than a thirty-two step approximation of it.
      That retires the cross-fade, which existed only to turn the steps between bands
      into ramps — there are no bands to step between.
   2. **240 spans and 120 layout objects out of the document**, 16.5% of every element
      on the configurator's page.
   3. **A warped bitmap the pack composite can draw.** It does not need one today: at
      the 120px the cart renders, the bow is about a third of a pixel and the composite
      clips the paper's outline to the parabola and draws the printing flat (`D-123`).
      If the composite is ever wanted large, the bitmap is here.

   **The curve.** Solving for the transform that maps the picture's own [0, 100] onto
   the mask's [0.9(1−p), 97.75+2.25p] at each column gives a scale of
   `0.9685 + 0.0315p` and a shift of `−0.675% + 0.675%p`, with `p = 1 − (2u−1)²`.

   **The mask states the same curve a second time**, as the SVG path in
   `--sdw-label-bow-mask` (`snippets/brand-tokens.liquid` §4e), and it has to, because
   a data URI cannot read a custom property. Those two are the only two copies and
   **they move together or the printing stops meeting the paper's edge.**

   **512×768, and one bake serves every breakpoint and both surfaces.** The largest
   the configurator's lead bottle ever reaches is 465×738 device pixels, at a 500px
   viewport and a device ratio of 3; 1440 at 2× is 380×604, and the hero's label is
   smaller than either. The warp is a pure vertical remapping stated in fractions of
   the box's own height and the horizontal axis is untouched, so it commutes with the
   stretch to whatever box it lands in — which is why one bitmap serves the lead
   bottle, the smaller ones behind it, and the hero alike, exactly as the stretched
   mask does.

   **Every failure returns null rather than throwing**, and both callers have the same
   answer for it: the flat picture inside the bowed paper, which is what the pre-warp
   build shipped. A label that is not curved beats no label.
   =================================================================== */

(() => {
  if (window.SDW_LABEL_WARP) return;

  const W = 512;
  const H = 768;

  /* The bitmap has to be CORS-clean or the canvas taints and `toBlob` throws, which
     is why this fetches rather than pointing an `<img>` at the URL. Shopify's CDN
     serves both shop files and theme assets with the headers this needs. */
  const bitmapFor = async (src) => {
    const res = await fetch(src, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error('fetch failed');
    return await createImageBitmap(await res.blob());
  };

  const toCanvas = (bitmap) => {
    const sw = bitmap.width;
    const sh = bitmap.height;
    if (!sw || !sh) return null;

    /* The horizontal resample happens once, into a full-height buffer, so the loop
       below can take an integer one-pixel source column. Sampling a fractional source
       rect 512 times instead would ask the filter for a sub-pixel window on every
       column, which is where seams come from. */
    const flat = document.createElement('canvas');
    flat.width = W;
    flat.height = sh;
    const fctx = flat.getContext('2d');
    if (!fctx) return null;
    fctx.imageSmoothingEnabled = true;
    fctx.imageSmoothingQuality = 'high';
    fctx.drawImage(bitmap, 0, 0, W, sh);

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    /* One output column at a time, one pixel wide and on an integer boundary, so
       there is nothing to blend across and no join to hide. `u` is the column's centre
       across the label, `p` how far round the cylinder it sits — 1 at the centre, 0 at
       either edge. */
    for (let x = 0; x < W; x += 1) {
      const u = (x + 0.5) / W;
      const d = 2 * u - 1;
      const p = 1 - d * d;
      const scale = 0.9685 + 0.0315 * p;
      const shift = -0.00675 + 0.00675 * p;
      const dh = scale * H;
      /* The CSS this replaces scaled about the box's centre and then translated, so
         the top edge lands at 0.9% of the height at the corners and at 0 in the
         middle — the mask's own path. */
      const dy = H * (0.5 - scale / 2 + shift);
      ctx.drawImage(flat, x, 0, 1, sh, x, dy, 1, dh);
    }

    return canvas;
  };

  const toBlob = (canvas) =>
    new Promise((resolve) => {
      try {
        /* WebP for the alpha the bowed edges need, at a quality that keeps the
           printing crisp; a browser that cannot encode it hands back a PNG of its own
           accord, which is equally fine here. */
        canvas.toBlob(resolve, 'image/webp', 0.95);
      } catch (_) {
        resolve(null);
      }
    });

  /* The whole job for a caller that has a URL and wants a URL — which is the hero.
     `bottle-stage.js` does not use this: it owns a bitmap cache, a revocation schedule
     and a token that cancels a bake over a cleared stage, none of which belong in a
     shared file, and it calls `toCanvas` on a bitmap it is already holding. */
  const toUrl = async (src) => {
    if (!src) return null;
    try {
      const canvas = toCanvas(await bitmapFor(src));
      if (!canvas) return null;
      const blob = await toBlob(canvas);
      return blob ? URL.createObjectURL(blob) : null;
    } catch (err) {
      console.warn('[SDW] label warp bake failed:', err);
      return null;
    }
  };

  window.SDW_LABEL_WARP = { W, H, toCanvas, toBlob, toUrl };
})();
